import { useEffect, useState } from "react";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useNavigationType,
} from "react-router-dom";
import { api, ApiError } from "./api";
import LoginView from "./routes/LoginView";
import ListView from "./routes/ListView";
import CreateView from "./routes/CreateView";
import LabelView from "./routes/LabelView";
import ModelsView from "./routes/ModelsView";
import SetupDetail from "./routes/SetupDetail";

type AuthState = "unknown" | "open" | "in" | "out";

type Theme = "dark" | "light";

function readTheme(): Theme {
  if (typeof window === "undefined") return "dark";
  const saved = localStorage.getItem("theme");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark";
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>("unknown");
  const [theme, setTheme] = useState<Theme>(readTheme);
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Reset scroll on forward navigation (PUSH/REPLACE) so a tap on a list
  // card near the bottom doesn't land the detail page already scrolled past
  // the chart. On back/forward (POP) we leave the browser to restore the
  // previous scroll position, so the list reappears where the user left it.
  useEffect(() => {
    if (navType !== "POP") window.scrollTo(0, 0);
  }, [location.pathname, navType]);

  // Probe a protected endpoint to discover whether we're already signed in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const health = await api.health().catch(() => null);
      if (cancelled) return;
      if (health && !health.auth_required) {
        setAuth("open");
        return;
      }
      try {
        await api.setups({ limit: 1 });
        if (!cancelled) setAuth("in");
      } catch (e) {
        if (cancelled) return;
        setAuth(e instanceof ApiError && e.status === 401 ? "out" : "out");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (auth === "unknown") {
    return <div className="app-main muted">Loading…</div>;
  }
  if (auth === "out" && location.pathname !== "/login") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="app">
      {auth !== "out" && (
        <nav className="app-nav">
          <NavLink to="/" end className={({ isActive }) => (isActive ? "active" : "")}>
            List
          </NavLink>
          <NavLink to="/label" className={({ isActive }) => (isActive ? "active" : "")}>
            Label
          </NavLink>
          <NavLink to="/create" className={({ isActive }) => (isActive ? "active" : "")}>
            Create
          </NavLink>
          <NavLink to="/models" className={({ isActive }) => (isActive ? "active" : "")}>
            Models
          </NavLink>
          <div id="nav-slot" className="nav-slot" />
          <span style={{ flex: 1 }} />
          <button
            className="theme-toggle"
            title={theme === "dark" ? "Switch to light" : "Switch to dark"}
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
          {auth === "in" && (
            <button
              onClick={async () => {
                await api.logout();
                setAuth("out");
                navigate("/login", { replace: true });
              }}
            >
              Sign out
            </button>
          )}
        </nav>
      )}
      <main className="app-main">
        <Routes>
          <Route
            path="/login"
            element={
              <LoginView
                onAuthed={() => {
                  setAuth("in");
                  const from = (location.state as { from?: string } | null)?.from ?? "/";
                  navigate(from, { replace: true });
                }}
              />
            }
          />
          <Route path="/" element={<ListView />} />
          <Route path="/detail" element={<SetupDetail />} />
          <Route path="/label" element={<LabelView />} />
          <Route path="/create" element={<CreateView />} />
          <Route path="/models" element={<ModelsView />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
